/**
 * Deploys: how this repository reaches production (and staging), on the
 * Repositories tab of Connections. The same for every Team: Harper reads it to
 * know which pull requests each production deploy shipped.
 *
 * One folded row per source. A repository with none gets what the detector
 * found in its workflows, offered with the reason, and saved only when a
 * person says "Use this". The job picker lists the names GitHub reports for
 * the workflow's latest run, so a reusable workflow's `caller / callee` job is
 * pickable as it will be matched; Check shows the deploys a source matches
 * before it is saved.
 */
import { useEffect, useState } from "react";
import type {
  DeploySource,
  DeploySourceCheck,
  DeploySourceDetection,
  DeploySourceEnvironment,
  StoredDeploySource,
} from "@yagni/shared";
import { Button } from "../ui/index.js";
import { SettingsRow } from "../workers/SettingsRow.js";
import { Row } from "./environmentControls.js";
import { asInput, type useRepositoryDeploys } from "./useRepositoryDeploys.js";

type DeploysState = ReturnType<typeof useRepositoryDeploys>;

const ENVIRONMENT_LABEL: Record<DeploySourceEnvironment, string> = {
  production: "Production",
  staging: "Staging",
};

function fileName(path: string): string {
  return path.split("/").pop() ?? path;
}

/** "deploy-fly-production.yml → job "Deploy to Production"". */
export function deploySourceSummary(source: DeploySource): string {
  return `${fileName(source.source.workflowPath)} → job "${source.source.jobName}"`;
}

function rowLabel(source: DeploySource): string {
  return source.label
    ? `${ENVIRONMENT_LABEL[source.environment]} · ${source.label}`
    : ENVIRONMENT_LABEL[source.environment];
}

function formatDeployTime(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime())
    ? iso
    : date.toLocaleString(undefined, {
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
}

/** What the check found, in one line. */
function checkMessage(checked: DeploySourceCheck, jobName: string): string {
  if (checked.deploys.length > 0) {
    return checked.deploys
      .map((d) => `${d.sha.slice(0, 7)} · ${formatDeployTime(d.completedAt)}`)
      .join("  ·  ");
  }
  if (checked.unmatched) return `No run of this workflow has a job named "${jobName}".`;
  return "No deploys matched yet.";
}

/** Which environment the source deploys to, and which service of a monorepo. */
function TargetRows({
  environment,
  label,
  onEnvironment,
  onLabel,
}: {
  environment: DeploySourceEnvironment;
  label: string;
  onEnvironment: (value: DeploySourceEnvironment) => void;
  onLabel: (value: string) => void;
}) {
  return (
    <>
      <Row title="Environment" help="Production is what Harper reports as shipped.">
        <span className="wrk-srow-controls" role="radiogroup" aria-label="Environment">
          {(["production", "staging"] as const).map((value) => (
            <label key={value} className="wrk-perm-label">
              <input
                type="radio"
                name="deploy-environment"
                value={value}
                checked={environment === value}
                onChange={() => onEnvironment(value)}
              />
              {ENVIRONMENT_LABEL[value]}
            </label>
          ))}
        </span>
      </Row>
      <Row title="Service" help="Only for a repository that deploys several services separately.">
        <span className="wrk-srow-controls">
          <input
            aria-label="Service"
            placeholder="web"
            value={label}
            maxLength={60}
            onChange={(event) => onLabel(event.target.value)}
          />
        </span>
      </Row>
    </>
  );
}

function SourceEditor({
  initial,
  detection,
  busy,
  onCheck,
  onSave,
  onCancel,
  onRemove,
}: {
  initial: DeploySource | null;
  detection: DeploySourceDetection | null;
  busy: boolean;
  onCheck: (input: { workflowPath: string; jobName?: string }) => Promise<DeploySourceCheck | null>;
  onSave: (source: DeploySource) => void;
  onCancel: () => void;
  onRemove?: () => void;
}) {
  const [environment, setEnvironment] = useState<DeploySourceEnvironment>(
    initial?.environment ?? "production",
  );
  const [label, setLabel] = useState(initial?.label ?? "");
  const [workflowPath, setWorkflowPath] = useState(initial?.source.workflowPath ?? "");
  const [jobName, setJobName] = useState(initial?.source.jobName ?? "");
  const [jobNames, setJobNames] = useState<string[] | null>(null);
  const [checked, setChecked] = useState<DeploySourceCheck | null>(null);
  const [checking, setChecking] = useState(false);

  const workflows = detection?.workflows ?? [];

  // The job names GitHub reports for this workflow's latest run.
  useEffect(() => {
    setJobNames(null);
    setChecked(null);
    if (!workflowPath) return;
    let live = true;
    void onCheck({ workflowPath }).then((result) => {
      if (live) setJobNames(result?.jobNames ?? []);
    });
    return () => {
      live = false;
    };
  }, [workflowPath, onCheck]);

  const fromYaml = workflows.find((w) => w.path === workflowPath)?.jobs.map((j) => j.name) ?? [];
  const options = [...new Set([...(jobNames ?? []), ...fromYaml, ...(jobName ? [jobName] : [])])];
  const source: DeploySource | null =
    workflowPath && jobName
      ? {
          environment,
          label: label.trim() || null,
          source: { kind: "actions_workflow", workflowPath, jobName },
        }
      : null;

  const runCheck = async () => {
    if (!source) return;
    setChecking(true);
    setChecked(await onCheck({ workflowPath, jobName }));
    setChecking(false);
  };

  return (
    <div data-testid="deploy-source-editor">
      <TargetRows
        environment={environment}
        label={label}
        onEnvironment={setEnvironment}
        onLabel={setLabel}
      />
      <Row title="Workflow" help="The GitHub Actions workflow that deploys.">
        <span className="wrk-srow-controls">
          <select
            aria-label="Workflow"
            value={workflowPath}
            onChange={(event) => {
              setWorkflowPath(event.target.value);
              setJobName("");
            }}
          >
            <option value="">Choose a workflow</option>
            {workflows.map((workflow) => (
              <option key={workflow.path} value={workflow.path}>
                {workflow.name} ({fileName(workflow.path)})
              </option>
            ))}
            {workflowPath && !workflows.some((w) => w.path === workflowPath) && (
              <option value={workflowPath}>{fileName(workflowPath)}</option>
            )}
          </select>
        </span>
      </Row>
      <Row
        title="Job"
        help="The job whose success is the deploy, as GitHub names it in the latest run."
      >
        <span className="wrk-srow-controls">
          <select
            aria-label="Job"
            value={jobName}
            disabled={!workflowPath}
            onChange={(event) => setJobName(event.target.value)}
          >
            <option value="">
              {workflowPath && jobNames === null ? "Reading the latest run…" : "Choose a job"}
            </option>
            {options.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        </span>
      </Row>
      <Row title="Check" help="The latest deploys this source matches.">
        <span className="wrk-srow-controls">
          <Button
            variant="secondary"
            size="sm"
            disabled={!source || checking}
            loading={checking}
            onClick={runCheck}
          >
            Check
          </Button>
          {checked && (
            <span className="wrk-srow-help" data-testid="deploy-source-check" role="status">
              {checkMessage(checked, jobName)}
            </span>
          )}
        </span>
      </Row>
      <div className="wrk-srow">
        <span className="wrk-srow-controls">
          <Button
            disabled={!source || busy}
            loading={busy}
            onClick={() => source && onSave(source)}
          >
            Save
          </Button>
          <Button variant="ghost" disabled={busy} onClick={onCancel}>
            Cancel
          </Button>
          {onRemove && (
            <Button variant="ghost" disabled={busy} onClick={onRemove}>
              Remove
            </Button>
          )}
        </span>
      </div>
    </div>
  );
}

/**
 * The deploy sources as rows of the sheet's How it ships list: one per
 * source, the detector's suggestions, and the row that adds one.
 */
export function RepositoryDeploys({ repoId, deploys }: { repoId: string; deploys: DeploysState }) {
  const [open, setOpen] = useState<string | null>(null);
  const { sources, detection } = deploys;

  useEffect(() => setOpen(null), [repoId]);

  // The editor's workflow list needs the detector's read.
  useEffect(() => {
    if (open && !detection) void deploys.detect();
  }, [open, detection, deploys.detect]);

  const keep = (except: StoredDeploySource | null) =>
    sources.filter((s) => s !== except).map(asInput);

  const saveOne = async (replacing: StoredDeploySource | null, next: DeploySource) => {
    if (await deploys.save([...keep(replacing), next])) setOpen(null);
  };

  const suggestions = sources.length === 0 ? (detection?.suggestions ?? []) : [];

  return (
    <>
      {deploys.error && (
        <p role="alert" className="qa-notice">
          {deploys.error}
        </p>
      )}
      {sources.map((source) => (
        <SettingsRow
          key={source.id}
          section={`deploy-${source.id}`}
          testId="deploy-source-row"
          label={`Deploy · ${rowLabel(source)}`}
          summary={deploySourceSummary(source)}
          open={open === source.id}
          onToggle={() => setOpen(open === source.id ? null : source.id)}
        >
          {open === source.id && (
            <SourceEditor
              initial={source}
              detection={detection}
              busy={deploys.busy}
              onCheck={deploys.check}
              onSave={(next) => void saveOne(source, next)}
              onCancel={() => setOpen(null)}
              onRemove={() => void deploys.save(keep(source)).then((ok) => ok && setOpen(null))}
            />
          )}
        </SettingsRow>
      ))}

      {suggestions.map((suggestion) => (
        <div
          className="wrk-srow"
          key={`${suggestion.environment}-${suggestion.source.workflowPath}`}
          data-testid="deploy-source-suggestion"
        >
          <span className="wrk-srow-text">
            <span className="wrk-srow-title">
              Detected · {rowLabel(suggestion)}: {deploySourceSummary(suggestion)}
            </span>
            <span className="wrk-srow-help">{suggestion.reason}</span>
          </span>
          <span className="wrk-srow-controls">
            <Button
              variant="secondary"
              size="sm"
              disabled={deploys.busy}
              onClick={() => void deploys.save([...keep(null), asInput(suggestion)])}
            >
              Use this
            </Button>
          </span>
        </div>
      ))}

      <SettingsRow
        section="deploy-add"
        testId="deploy-source-add"
        label={sources.length ? "Add a deploy" : "Deploys"}
        summary={
          deploys.loaded && sources.length === 0 && detection && suggestions.length === 0 ? (
            <span data-testid="deploy-sources-empty">
              No deploy workflow found. Harper can't tell what reached production. Add a deploy
              source.
            </span>
          ) : sources.length ? (
            "Another deploy source"
          ) : (
            "How this repository reaches production. Add a deploy source."
          )
        }
        open={open === "add"}
        onToggle={() => setOpen(open === "add" ? null : "add")}
      >
        {open === "add" && (
          <SourceEditor
            initial={null}
            detection={detection}
            busy={deploys.busy}
            onCheck={deploys.check}
            onSave={(next) => void saveOne(null, next)}
            onCancel={() => setOpen(null)}
          />
        )}
      </SettingsRow>
    </>
  );
}
